import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  TrendingDown, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  DollarSign,
  BarChart3,
  FileCheck,
  Target
} from "lucide-react";

const MetricCard = ({ label, value, subtext, icon: Icon, color = "slate" }) => {
  const colorClasses = {
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-200",
    amber: "text-amber-600 bg-amber-50 border-amber-200",
    red: "text-red-600 bg-red-50 border-red-200",
    blue: "text-blue-600 bg-blue-50 border-blue-200",
    slate: "text-slate-600 bg-slate-50 border-slate-200",
  };
  
  return (
    <div className={`border rounded-md p-3 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} />
        <span className="text-sm uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="text-xl font-bold">{value}</div>
      {subtext && <div className="text-sm opacity-70">{subtext}</div>}
    </div>
  );
};

const RiskBadge = ({ level }) => {
  const styles = {
    low: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    high: "bg-red-100 text-red-700",
  };
  return (
    <Badge className={`text-sm ${styles[level] || styles.medium}`}>
      {level?.toUpperCase()} RISK
    </Badge>
  );
};

const Section = ({ title, icon: Icon, children, color = "slate" }) => {
  const bgColors = {
    slate: "bg-slate-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
    emerald: "bg-emerald-50",
  };
  
  return (
    <div className={`rounded-md border border-slate-200 overflow-hidden`}>
      <div className={`${bgColors[color]} px-4 py-2 border-b border-slate-200 flex items-center gap-2`}>
        <Icon size={14} className="text-slate-600" />
        <span className="text-base font-bold uppercase tracking-wider text-slate-700">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

export default function BankAnalysis({ data }) {
  if (!data) return null;
  if (data.error) return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
      <div className="border-b border-slate-200 p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
        <span className="text-sm font-bold uppercase tracking-wider text-blue-800">Bank / Lender Analysis</span>
      </div>
      <CardContent className="p-6 text-center">
        <Clock size={32} className="mx-auto text-amber-400 mb-3" />
        <p className="text-base font-medium text-slate-700">Bank Analysis Temporarily Unavailable</p>
        <p className="text-sm text-slate-400 mt-1">This section will be generated when the AI service is back online.</p>
      </CardContent>
    </Card>
  );
  
  const {
    executive_summary,
    asset_quality_score,
    asset_quality_grade,
    market_position,
    depreciation_analysis,
    loan_recommendations,
    npa_risk_model,
    value_projections,
    monitoring_schedule,
  } = data;

  const getScoreColor = (score) => {
    if (score >= 80) return "emerald";
    if (score >= 60) return "amber";
    return "red";
  };

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="bank-analysis-card">
      <div className="border-b border-slate-200 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-blue-600" />
          <span className="text-base font-bold uppercase tracking-wider text-blue-800">
            Bank / Lender Analysis
          </span>
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-sm">
            ASSET FINANCING VIEW
          </Badge>
        </div>
        {asset_quality_score && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-blue-600">
              Asset Quality: {asset_quality_score}/100
            </span>
            <Badge className={`text-sm ${
              asset_quality_score >= 80 ? 'bg-emerald-100 text-emerald-700' : 
              asset_quality_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
            }`}>
              Grade {asset_quality_grade}
            </Badge>
          </div>
        )}
      </div>
      
      <CardContent className="p-4 space-y-4">
        {/* Executive Summary */}
        {executive_summary && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <FileCheck size={14} className="text-blue-600" />
              <span className="text-base font-bold text-blue-800 uppercase">Executive Summary</span>
            </div>
            <p className="text-sm text-slate-700">{executive_summary}</p>
          </div>
        )}

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard 
            label="Max LTV" 
            value={`${loan_recommendations?.max_ltv_percent || loan_recommendations?.max_ltv_recommended_percent || 0}%`}
            subtext="Loan-to-Value"
            icon={DollarSign}
            color={(loan_recommendations?.max_ltv_percent || loan_recommendations?.max_ltv_recommended_percent || 0) >= 80 ? "emerald" : "amber"}
          />
          <MetricCard 
            label="Tenure" 
            value={`${loan_recommendations?.recommended_tenure_months || 0}M`}
            subtext="Recommended"
            icon={Clock}
            color="blue"
          />
          <MetricCard 
            label="NPA Risk" 
            value={npa_risk_model?.final_npa_probability_percent || 0}
            subtext={`% probability`}
            icon={AlertTriangle}
            color={getScoreColor(100 - (npa_risk_model?.final_npa_probability_percent || 50))}
          />
          <MetricCard 
            label="Brand" 
            value={market_position?.resale_liquidity?.toUpperCase() || "N/A"}
            subtext="Resale Liquidity"
            icon={Shield}
            color={market_position?.resale_liquidity === "excellent" ? "emerald" : 
                   market_position?.resale_liquidity === "good" ? "blue" : "amber"}
          />
        </div>

        {/* Depreciation Analysis */}
        <Section title="Depreciation Analysis" icon={TrendingDown} color="amber">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-slate-500 uppercase mb-1">Standard Depreciation</div>
              <div className="text-lg font-bold text-slate-700">
                {depreciation_analysis?.standard_rate_percent || depreciation_analysis?.standard_annual_depreciation_percent || 0}% <span className="text-sm font-normal">cumulative</span>
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-500 uppercase mb-1">Condition-Adjusted</div>
              <div className="text-lg font-bold text-amber-600">
                {depreciation_analysis?.condition_adjusted_rate_percent || depreciation_analysis?.condition_adjusted_depreciation_percent || 0}% <span className="text-sm font-normal">cumulative</span>
              </div>
            </div>
          </div>
          {depreciation_analysis?.justification && (
            <p className="text-sm text-slate-600 italic mt-2">{depreciation_analysis.justification}</p>
          )}
          {depreciation_analysis?.depreciation_curve_data && (
            <div className="mt-3">
              <div className="text-sm text-slate-500 uppercase mb-2">Depreciation Curve:</div>
              <div className="flex gap-2">
                {depreciation_analysis.depreciation_curve_data.map((item, idx) => (
                  <div key={idx} className="flex-1 p-2 bg-slate-50 rounded-md text-center text-sm">
                    <div className="text-slate-400">Yr {item.year}</div>
                    <div className="font-bold text-slate-700">{item.standard}%</div>
                    <div className="text-amber-600 text-xs">Adj: {item.adjusted}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Loan Recommendations */}
        <Section title="Loan Recommendations" icon={DollarSign} color="blue">
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-2 bg-blue-50 rounded-md">
                <div className="text-sm text-blue-600 uppercase">Max LTV</div>
                <div className="text-xl font-bold text-blue-800">{loan_recommendations?.max_ltv_percent || loan_recommendations?.max_ltv_recommended_percent}%</div>
              </div>
              <div className="p-2 bg-blue-50 rounded-md">
                <div className="text-sm text-blue-600 uppercase">Tenure</div>
                <div className="text-xl font-bold text-blue-800">{loan_recommendations?.recommended_tenure_months}M</div>
              </div>
              <div className="p-2 bg-blue-50 rounded-md">
                <div className="text-sm text-blue-600 uppercase">Risk Premium</div>
                <div className="text-xl font-bold text-blue-800">+{loan_recommendations?.risk_premium_bps || loan_recommendations?.risk_adjusted_interest_premium_bps}bps</div>
              </div>
            </div>
            {loan_recommendations?.tenure_justification && (
              <p className="text-sm text-slate-600 italic">{loan_recommendations.tenure_justification}</p>
            )}
            {loan_recommendations?.premium_justification && (
              <p className="text-sm text-slate-600 italic">{loan_recommendations.premium_justification}</p>
            )}
            {loan_recommendations?.emi_buffer_recommendation_percent && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">EMI Buffer:</span>
                <Badge className="bg-blue-100 text-blue-700 text-sm">{loan_recommendations.emi_buffer_recommendation_percent}%</Badge>
              </div>
            )}
          </div>
        </Section>

        {/* NPA Risk Assessment */}
        <Section title="NPA Risk Assessment" icon={AlertTriangle} color="amber">
          <div className="grid grid-cols-3 gap-3 mb-3 text-center">
            <div>
              <div className="text-sm text-slate-500 uppercase">Base NPA Rate</div>
              <Badge className="text-sm bg-amber-100 text-amber-700">
                {npa_risk_model?.base_npa_rate_percent}%
              </Badge>
            </div>
            <div>
              <div className="text-sm text-slate-500 uppercase">Condition Adj.</div>
              <Badge className="text-sm bg-amber-100 text-amber-700">
                +{npa_risk_model?.condition_adjustment_percent}%
              </Badge>
            </div>
            <div>
              <div className="text-sm text-slate-500 uppercase">Final NPA Prob.</div>
              <Badge className={`text-sm ${
                (npa_risk_model?.final_npa_probability_percent || 5) <= 3 ? 'bg-emerald-100 text-emerald-700' :
                (npa_risk_model?.final_npa_probability_percent || 5) <= 6 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
              }`}>
                {npa_risk_model?.final_npa_probability_percent}%
              </Badge>
            </div>
          </div>
          
          {npa_risk_model?.risk_factors_weighted && (
            <div className="mb-3">
              <div className="text-sm text-slate-500 uppercase mb-2">Risk Factors (Weighted):</div>
              <div className="space-y-1">
                {npa_risk_model.risk_factors_weighted.map((rf, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm p-1.5 bg-slate-50 rounded">
                    <span className="capitalize text-slate-600">{rf.factor?.replace(/_/g, ' ')}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400">W: {rf.weight}%</span>
                      <span className="text-slate-700 font-medium">Score: {rf.score}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {npa_risk_model?.npa_reduction_potential_percent && (
            <div className="flex items-center gap-2 text-sm p-2 bg-emerald-50 rounded">
              <CheckCircle size={14} className="text-emerald-600" />
              <span className="text-emerald-700">NPA reduction potential: {npa_risk_model.npa_reduction_potential_percent}% with this inspection report</span>
            </div>
          )}
        </Section>

        {/* Asset Health Monitoring */}
        <Section title="Asset Health Monitoring" icon={Target} color="emerald">
          <div className="flex items-center gap-4 mb-3">
            <div>
              <div className="text-sm text-slate-500 uppercase">Inspection Frequency</div>
              <div className="text-lg font-bold text-emerald-600">
                Every {monitoring_schedule?.inspection_frequency_months} months
              </div>
            </div>
          </div>
          
          {monitoring_schedule?.key_checkpoints && (
            <div className="mb-2">
              <div className="text-sm text-slate-500 uppercase mb-1">Key Checkpoints:</div>
              <div className="flex flex-wrap gap-1">
                {monitoring_schedule.key_checkpoints.map((metric, idx) => (
                  <Badge key={idx} className="bg-slate-100 text-slate-600 text-sm">{metric}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {monitoring_schedule?.alert_thresholds && (
            <div>
              <div className="text-sm text-slate-500 uppercase mb-1">Alert Thresholds:</div>
              <div className="flex flex-wrap gap-1">
                {monitoring_schedule.alert_thresholds.map((cov, idx) => (
                  <Badge key={idx} className="bg-amber-50 text-amber-600 text-sm">{cov}</Badge>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Value Projections */}
        <Section title="Value Projections" icon={BarChart3} color="slate">
          {value_projections?.projection_data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              {value_projections.projection_data.map((proj, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-md border border-slate-200">
                  <div className="text-sm text-slate-500 uppercase">{proj.months} Months</div>
                  <div className="text-lg font-bold text-emerald-600">{proj.with_maintenance}%</div>
                  <div className="text-xs text-slate-400">w/ maintenance</div>
                  <div className="text-sm font-medium text-amber-600">{proj.without_maintenance}%</div>
                  <div className="text-xs text-slate-400">w/o maintenance</div>
                </div>
              ))}
            </div>
          )}
          {value_projections?.breakeven_analysis && (
            <div className="mt-3 grid grid-cols-3 gap-3 text-center">
              <div className="p-2 bg-slate-50 rounded text-sm">
                <div className="text-slate-500">Best Case</div>
                <div className="font-bold">{value_projections.breakeven_analysis.recovery_months_best_case} mo</div>
              </div>
              <div className="p-2 bg-slate-50 rounded text-sm">
                <div className="text-slate-500">Worst Case</div>
                <div className="font-bold">{value_projections.breakeven_analysis.recovery_months_worst_case} mo</div>
              </div>
              <div className="p-2 bg-slate-50 rounded text-sm">
                <div className="text-slate-500">Salvage Value</div>
                <div className="font-bold">{value_projections.breakeven_analysis.salvage_value_percent}%</div>
              </div>
            </div>
          )}
        </Section>
      </CardContent>
    </Card>
  );
}
